// Recorder-related notices: unsupported browser, microphone errors, muted microphone,
// screen-reader announcements.
import { useEffect, useRef, useState } from 'react';
import { MSG_MUTED, type RecorderState } from '../lib/recorder';
import { Icon } from './Icon';

export function UnsupportedNotice({ alternative }: { alternative: string }) {
  const insecure = typeof window !== 'undefined' && window.isSecureContext === false;
  return (
    <div className="notice notice--warning" role="note">
      <Icon name="alert" size={20} className="notice__icon" />
      <div>
        <p className="notice__title">Snemanje v tem brskalniku ni mogoče.</p>
        <p>
          {insecure
            ? 'Stran ni odprta prek varne povezave (HTTPS), zato brskalnik ne dovoli uporabe mikrofona. '
            : 'Uporabite novejši Chrome, Safari, Edge ali Firefox. '}
          {alternative}
        </p>
      </div>
    </div>
  );
}

export function RecorderErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="notice notice--error" role="alert">
      <Icon name="alert" size={20} className="notice__icon" />
      <div className="notice__content">
        <p>{message}</p>
        {onRetry && (
          <button type="button" className="btn btn--secondary btn--small" onClick={onRetry}>
            <Icon name="refresh" size={18} /> Poskusi znova
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Warning while the system mutes the microphone during a recording (a call on iOS, another app
 * took the microphone): silence is recorded meanwhile. The live region stays mounted, so screen
 * readers announce the warning when it appears.
 */
export function MutedNotice({ muted }: { muted: boolean }) {
  return (
    <div className="live-slot" role="status" aria-live="polite">
      {muted && (
        <div className="notice notice--warning">
          <Icon name="alert" size={20} className="notice__icon" />
          <p className="notice__content">{MSG_MUTED}</p>
        </div>
      )}
    </div>
  );
}

const STATE_TEXT: Partial<Record<RecorderState, string>> = {
  preparing: 'Vklapljam mikrofon.',
  recording: 'Snemanje poteka.',
  paused: 'Snemanje je začasno ustavljeno.',
  stopping: 'Snemanje je ustavljeno, shranjujem posnetek.',
};

/** Visually hidden live region that announces recorder state changes to screen readers. */
export function RecorderAnnouncer({ state }: { state: RecorderState }) {
  const [text, setText] = useState('');
  const previous = useRef<RecorderState>(state);
  useEffect(() => {
    const before = previous.current;
    previous.current = state;
    if (before === state) return;
    const next = STATE_TEXT[state] ?? (before === 'stopping' || before === 'recording' ? 'Snemanje je končano.' : '');
    if (next) setText(next);
  }, [state]);
  return (
    <div className="sr-only" aria-live="assertive" aria-atomic="true">
      {text}
    </div>
  );
}
